import { Vector2 } from "../../common/vector2.js";
import { Game } from "../../game.js";
import { ImgID } from "../../image_loader/img_id.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../transforms/transform_straight_move.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { NotificationBadge } from "../common_ui_elements/notification_badge.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";

export class NavigationItem extends UIElement
{
  private img_id: ImgID;
  private text: string;

  private is_calendar: boolean;
  private is_locked: boolean;

  private image_el!: UIImage;
  private text_el!: UITextElement;
  private msg_el?: UIImage;
  private img_bg_el?: UIImage;
  private indicator?: NotificationBadge;

  private readonly ITEM_MAIN_SCALE = 1.5;
  private readonly ITEM_Y_UP = 90;
  private readonly MOBILE_DEFAULT_ITEM_DOWN = 15;
  private readonly DEFAULT_TEXT_UP = 20;
  private readonly LOCK_W = 140;
  private readonly LOCK_H = 140;
  private readonly INDICATOR_SIZE = 52;
  private readonly INDICATOR_MAX_COUNT = 99;
  private readonly INDICATOR_INSET_X = 6;
  private readonly INDICATOR_INSET_Y = 4;

  private default_img_y!: number;
  private default_text_y!: number;

  private main_img_y!: number;
  private main_text_y!: number;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    img_id: ImgID,
    text: string,
    is_calendar: boolean,
    is_locked = false
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.img_id = img_id;
    this.text = text;

    this.is_calendar = is_calendar;
    this.is_locked = is_locked;

    this.createContent();
  }

  private createContent(): void
  {
    const mobile_default_down = Game.isMobile()
      ? LayoutScaler.yFromDesign( this.MOBILE_DEFAULT_ITEM_DOWN )
      : 0;
    const item_down = mobile_default_down;

    const w = LayoutScaler.widthFromDesign( 168 );
    const h = LayoutScaler.heightFromDesign( 168 );
    const text_h = LayoutScaler.heightFromDesign( 70 );

    const x = this.rect.getW2() - w / 2;
    const y = this.rect.getH2() - h / 2
      + item_down;

    const icon_w = this.is_locked ? LayoutScaler.widthFromDesign( this.LOCK_W ) : w;
    const icon_h = this.is_locked ? LayoutScaler.heightFromDesign( this.LOCK_H ) : h;
    const icon_x = x + ( w - icon_w ) / 2;
    const icon_y = y + ( h - icon_h ) / 2;
    const icon_id = this.is_locked ? ImgID.IconNavigationLock : this.img_id;

    const img = new UIImage( this.ctx, icon_x, icon_y, icon_w, icon_h, icon_id, {} );

    const text_under_icon_y = y + h + LayoutScaler.heightFromDesign( 20 );

    this.default_img_y = icon_y;
    this.default_text_y = text_under_icon_y - LayoutScaler.heightFromDesign( this.DEFAULT_TEXT_UP );

    const item_y_up = this.ITEM_Y_UP;

    this.main_img_y =
      this.default_img_y - LayoutScaler.yFromDesign( item_y_up ) - mobile_default_down;

    this.main_text_y = this.default_text_y;

    const text = new UITextElement(
      this.ctx,
      x - w / 2,
      this.default_text_y,
      w * 2,
      text_h,
      {
        localization_key: this.text,
        vertical_align: MyTextVerticalAlign.Top,
        text_align: MyTextAlign.Center,
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 35 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        auto_wrap: false,
        outline_color: ColorScheme.navigation_text_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 1 ),
        shadow_blur: LayoutScaler.widthFromDesign( 4 ),
        shadow_offset_y: LayoutScaler.heightFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent
      }
    );

    img.setClickable( false );
    text.setClickable( false );

    this.addChild( img );
    this.addChild( text );

    this.image_el = img;
    this.text_el = text;
  }

  public setIndicator( count: number ): void
  {
    const shown = Math.min( Math.max( 0, Math.floor( count ) ), this.INDICATOR_MAX_COUNT );

    if ( shown === 0 )
    {
      if ( this.indicator === undefined )
        return;

      this.image_el.removeChild( this.indicator );
      this.indicator = undefined;
      this.forceMakeShouldBeRendered();
      return;
    }

    if ( this.indicator !== undefined )
    {
      this.indicator.setCount( shown );
      this.forceMakeShouldBeRendered();
      return;
    }

    const size = LayoutScaler.widthFromDesign( this.INDICATOR_SIZE );
    const x = this.image_el.getRect().getW() - size - LayoutScaler.widthFromDesign( this.INDICATOR_INSET_X );
    const y = LayoutScaler.heightFromDesign( this.INDICATOR_INSET_Y );

    this.indicator = new NotificationBadge( this.ctx, x, y, size, size );
    this.indicator.setCount( shown );
    this.image_el.addChild( this.indicator, true );
    this.forceMakeShouldBeRendered();
  }

  public hideMsgIcon(): void
  {
    this.msg_el && this.removeChild( this.msg_el );
  }

  public doMain( is_init = false ): void
  {
    const img = this.image_el;
    const text = this.text_el;

    this.img_bg_el?.hideElement( is_init ? 0 : 150 );

    const scale_count = this.ITEM_MAIN_SCALE;

    const scale = new TransformScale(
      img,
      true,
      null,
      new Vector2( scale_count, scale_count ),
      performance.now(),
      is_init ? 0 : 150
    );

    const move_to = new TransformStraightMove(
      img,
      true,
      null,
      new Vector2(
        img.pixelPosForRender().x,
        this.main_img_y
      ),
      performance.now(),
      is_init ? 0 : 150
    )

    const move_to_text = new TransformStraightMove(
      text,
      true,
      null,
      new Vector2(
        text.pixelPosForRender().x,
        this.main_text_y
      ),
      performance.now(),
      is_init ? 0 : 150
    )

    const text_scale = new TransformScale(
      text,
      true,
      null,
      new Vector2( scale_count, scale_count ),
      performance.now(),
      is_init ? 0 : 150
    );

    img.addTransform( scale );
    img.addTransform( move_to );

    text.addTransform( text_scale );
    text.addTransform( move_to_text );
  }

  public doDefault(): void
  {
    const img = this.image_el;
    const text = this.text_el;

    this.img_bg_el?.showElement( 120 );

    const scale = new TransformScale(
      img,
      true,
      null,
      new Vector2( 1, 1 ),
      performance.now(),
      150
    );

    const move_to = new TransformStraightMove(
      img,
      true,
      null,
      new Vector2(
        img.pixelPosForRender().x,
        this.default_img_y
      ),
      performance.now(),
      150
    )

    const move_to_text = new TransformStraightMove(
      text,
      true,
      null,
      new Vector2(
        text.pixelPosForRender().x,
        this.default_text_y
      ),
      performance.now(),
      150
    )

    const text_scale = new TransformScale(
      text,
      true,
      null,
      new Vector2( 1, 1 ),
      performance.now(),
      150
    );

    img.addTransform( scale );
    img.addTransform( move_to );

    text.addTransform( text_scale );
    text.addTransform( move_to_text );
  }

  public getIconRect(): { x: number, y: number, width: number, height: number }
  {
    return this.image_el.getAbsoluteRect();
  }

  public name(): string
  {
    return "NavigationItem";
  }

  public update( now: number ): void
  {
    super.updateBasic( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
